/**
 * Admin V3 — Drivers — Document Upload Modal (Plan §2.2 M-F-10).
 * Posts multipart/form-data to /admin/drivers/{id}/documents.
 * C-H-2: backend enforces MIME / magic-byte / size / filename validation.
 * Client-side performs a friendly pre-check; backend is authoritative.
 */
import React, { useState, useRef } from 'react';
import { X, Upload, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { t } from '../../i18n';

const DOC_TYPES = [
  'permis_de_conduire',
  'carte_grise',
  'attestation_assurance',
  'capacite_professionnelle',
  'kbis',
  'casier_judiciaire',
  'justificatif_domicile',
  'identity_document',
];

const ALLOWED_MIME = new Set(['application/pdf', 'image/jpeg', 'image/png']);
const MAX_BYTES = 20 * 1024 * 1024;

const API_URL = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

const DocumentUploadModal = ({ driverId, onClose, onSuccess, locale = 'fr' }) => {
  const { getAuthHeaders } = useAuth();
  const fileInputRef = useRef(null);
  const [docType, setDocType] = useState('');
  const [file, setFile] = useState(null);
  const [expiry, setExpiry] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!docType) { setError(t('compliance.uploadModal.error.no_type', locale)); return; }
    if (!file)    { setError(t('compliance.uploadModal.error.no_file', locale)); return; }
    if (!ALLOWED_MIME.has(file.type)) {
      setError(t('compliance.uploadModal.error.mime', locale));
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(t('compliance.uploadModal.error.size', locale));
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('document_type', docType);
    if (expiry) fd.append('expiry_date', new Date(expiry).toISOString());

    setLoading(true);
    try {
      const headers = {
        ...getAuthHeaders(),
        'X-Idempotency-Key': `doc-upload-${driverId}-${Date.now()}`,
      };
      await axios.post(`${API_URL}/admin/drivers/${driverId}/documents`, fd, { headers });
      if (onSuccess) onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.reason) {
        setError(t(`compliance.uploadModal.error.${detail.reason}`, locale) || t('compliance.uploadModal.error.generic', locale));
      } else {
        setError(t('compliance.uploadModal.error.generic', locale));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center" data-testid="document-upload-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-[18px] font-semibold text-slate-900">{t('compliance.uploadModal.title', locale)}</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-700 mb-1">{t('compliance.uploadModal.field.type', locale)}</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              data-testid="upload-doctype"
            >
              <option value="">—</option>
              {DOC_TYPES.map((dt) => (
                <option key={dt} value={dt}>{t(`compliance.doctype.${dt}`, locale)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-700 mb-1">{t('compliance.uploadModal.field.file', locale)}</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm"
              data-testid="upload-file"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-700 mb-1">{t('compliance.uploadModal.field.expiry', locale)}</label>
            <input
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              data-testid="upload-expiry"
            />
          </div>
          {error && <div className="text-sm text-rose-700">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
            >
              {t('compliance.uploadModal.cancel', locale)}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="upload-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {t('compliance.uploadModal.submit', locale)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
